import * as THREE from "three";
import * as CANNON from "cannon-es";

// ============================================================================
// PHYSICS CONSTANTS
// ============================================================================

/** Default physics world settings */
export const PHYSICS_CONSTANTS = {
  /** Default gravity vector [x, y, z] */
  DEFAULT_GRAVITY: [0, -9.82, 0] as [number, number, number],
  /** Default timestep for physics simulation (in seconds) */
  DEFAULT_TIMESTEP: 1 / 60,
  /** Maximum number of solver iterations per timestep */
  DEFAULT_SOLVER_ITERATIONS: 10,
  /** Default tolerance for solver convergence */
  DEFAULT_SOLVER_TOLERANCE: 1e-7,
  /** Maximum number of substeps for stable simulation */
  MAX_SUBSTEPS: 10,
  /** Default linear damping for bodies */
  DEFAULT_LINEAR_DAMPING: 0.01,
  /** Default angular damping for bodies */
  DEFAULT_ANGULAR_DAMPING: 0.01,
  /** Default friction coefficient */
  DEFAULT_FRICTION: 0.3,
  /** Default restitution (bounciness) coefficient */
  DEFAULT_RESTITUTION: 0.3,
  /** Default density for bodies (kg/m³) */
  DEFAULT_DENSITY: 1000,
  /** Default mass for bodies (kg) */
  DEFAULT_MASS: 1,
  /** Threshold for considering a body at rest */
  SLEEP_SPEED_LIMIT: 0.1,
  /** Threshold for considering a body at rest (angular) */
  SLEEP_ANGULAR_SPEED_LIMIT: 0.1,
  /** Time before a body can sleep */
  SLEEP_TIME_LIMIT: 0.5,
  /** Default contact material friction */
  CONTACT_MATERIAL_FRICTION: 0.3,
  /** Default contact material restitution */
  CONTACT_MATERIAL_RESTITUTION: 0.3,
} as const;

/** Physics shape types */
export const PHYSICS_SHAPE_TYPES = {
  BOX: "Box",
  SPHERE: "Sphere",
  PLANE: "Plane",
  CYLINDER: "Cylinder",
  CONVEX_POLYHEDRON: "ConvexPolyhedron",
  TRIMESH: "Trimesh",
  HEIGHTFIELD: "Heightfield",
  PARTICLE: "Particle",
} as const;

/** Physics body types */
export const PHYSICS_BODY_TYPES = {
  /** Dynamic body - affected by forces, collisions, etc. */
  DYNAMIC: CANNON.Body.DYNAMIC,
  /** Static body - never moves */
  STATIC: CANNON.Body.STATIC,
  /** Kinematic body - moves but not affected by forces */
  KINEMATIC: CANNON.Body.KINEMATIC,
} as const;

/** Joint types */
export const JOINT_TYPES = {
  DISTANCE: "DistanceConstraint",
  HINGE: "HingeConstraint",
  LOCK: "LockConstraint",
  POINT_TO_POINT: "PointToPointConstraint",
  CONE_TWIST: "ConeTwistConstraint",
  SPRING: "Spring",
} as const;

/** Constraint types */
export const CONSTRAINT_TYPES = {
  ...JOINT_TYPES,
  SLIDER: "SliderConstraint",
  PRISMATIC: "PrismaticConstraint",
} as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Shape type identifier */
export type PhysicsShapeType =
  | (typeof PHYSICS_SHAPE_TYPES)[keyof typeof PHYSICS_SHAPE_TYPES];

/** Body type identifier */
export type PhysicsBodyType =
  | (typeof PHYSICS_BODY_TYPES)[keyof typeof PHYSICS_BODY_TYPES];

/** Joint type identifier */
export type JointType = (typeof JOINT_TYPES)[keyof typeof JOINT_TYPES];

/** Constraint type identifier */
export type ConstraintType =
  | (typeof CONSTRAINT_TYPES)[keyof typeof CONSTRAINT_TYPES];

/** Options for creating a physics world */
export type CreateWorldOptions = {
  /** Gravity vector [x, y, z] (default: [0, -9.82, 0]) */
  gravity?: [number, number, number];
  /** Timestep for physics simulation in seconds (default: 1/60) */
  timestep?: number;
  /** Maximum solver iterations (default: 10) */
  solverIterations?: number;
  /** Solver tolerance (default: 1e-7) */
  solverTolerance?: number;
  /** Enable broadphase (default: true) */
  enableBroadphase?: boolean;
  /** Enable sleeping (default: true) */
  enableSleeping?: boolean;
  /** World ID for identification */
  worldId?: string;
};

/** Options for creating a physics body */
export type CreateBodyOptions = {
  /** Body type: DYNAMIC, STATIC, or KINEMATIC */
  type?: PhysicsBodyType;
  /** Mass in kg (0 for static bodies) */
  mass?: number;
  /** Position [x, y, z] */
  position?: [number, number, number] | THREE.Vector3;
  /** Rotation as quaternion [x, y, z, w] or Euler [x, y, z] */
  rotation?:
    | [number, number, number, number]
    | [number, number, number]
    | THREE.Quaternion
    | THREE.Euler;
  /** Linear damping (0-1) */
  linearDamping?: number;
  /** Angular damping (0-1) */
  angularDamping?: number;
  /** Friction coefficient */
  friction?: number;
  /** Restitution (bounciness) coefficient */
  restitution?: number;
  /** Material name for contact materials */
  material?: string;
  /** Whether body can sleep */
  allowSleep?: boolean;
  /** Speed limit for sleep (applies to both linear and angular) */
  sleepSpeedLimit?: number;
  /** Group ID for collision filtering */
  collisionFilterGroup?: number;
  /** Group mask for collision filtering */
  collisionFilterMask?: number;
  /** Body ID for identification */
  bodyId?: string;
  /** User data attached to body */
  userData?: Record<string, unknown>;
};

/** Options for creating a box shape */
export type CreateBoxShapeOptions = {
  /** Half extents [x, y, z] */
  halfExtents: [number, number, number] | THREE.Vector3;
};

/** Options for creating a sphere shape */
export type CreateSphereShapeOptions = {
  /** Radius */
  radius: number;
};

/** Options for creating a plane shape */
export type CreatePlaneShapeOptions = {
  /** Normal vector [x, y, z] */
  normal?: [number, number, number] | THREE.Vector3;
};

/** Options for creating a cylinder shape */
export type CreateCylinderShapeOptions = {
  /** Radius top */
  radiusTop: number;
  /** Radius bottom */
  radiusBottom: number;
  /** Height */
  height: number;
  /** Number of segments */
  numSegments?: number;
};

/** Union of all shape creation options */
export type CreateShapeOptions =
  | CreateBoxShapeOptions
  | CreateSphereShapeOptions
  | CreatePlaneShapeOptions
  | CreateCylinderShapeOptions;

/** Options for syncing mesh/group with physics body */
export type SyncMeshOptions = {
  /** Whether to sync position */
  syncPosition?: boolean;
  /** Whether to sync rotation */
  syncRotation?: boolean;
  /** Whether to sync scale (requires custom handling) */
  syncScale?: boolean;
  /** Offset position [x, y, z] */
  positionOffset?: [number, number, number] | THREE.Vector3;
  /** Offset rotation as quaternion or euler */
  rotationOffset?:
    | [number, number, number, number]
    | [number, number, number]
    | THREE.Quaternion
    | THREE.Euler;
};

/** Options for applying velocity */
export type ApplyVelocityOptions = {
  /** Linear velocity [x, y, z] */
  linear?: [number, number, number] | THREE.Vector3;
  /** Angular velocity [x, y, z] */
  angular?: [number, number, number] | THREE.Vector3;
  /** Whether to add to existing velocity or set */
  add?: boolean;
};

/** Options for applying quaternion rotation */
export type ApplyQuaternionOptions = {
  /** Quaternion [x, y, z, w] */
  quaternion?: [number, number, number, number] | THREE.Quaternion;
  /** Euler angles [x, y, z] in radians */
  euler?: [number, number, number] | THREE.Euler;
  /** Whether to multiply with existing rotation or set */
  multiply?: boolean;
};

/** Options for distance constraint */
export type DistanceConstraintOptions = {
  /** Pivot point A [x, y, z] in body A's local space */
  pivotA?: [number, number, number] | THREE.Vector3;
  /** Pivot point B [x, y, z] in body B's local space */
  pivotB?: [number, number, number] | THREE.Vector3;
  /** Distance between pivots */
  distance?: number;
};

/** Options for hinge constraint */
export type HingeConstraintOptions = {
  /** Pivot point A [x, y, z] in body A's local space */
  pivotA?: [number, number, number] | THREE.Vector3;
  /** Pivot point B [x, y, z] in body B's local space */
  pivotB?: [number, number, number] | THREE.Vector3;
  /** Axis A [x, y, z] in body A's local space */
  axisA?: [number, number, number] | THREE.Vector3;
  /** Axis B [x, y, z] in body B's local space */
  axisB?: [number, number, number] | THREE.Vector3;
  /** Maximum angle (radians) */
  maxAngle?: number;
};

/** Options for point-to-point constraint */
export type PointToPointConstraintOptions = {
  /** Pivot point A [x, y, z] in body A's local space */
  pivotA?: [number, number, number] | THREE.Vector3;
  /** Pivot point B [x, y, z] in body B's local space */
  pivotB?: [number, number, number] | THREE.Vector3;
};

/** Options for spring constraint */
export type SpringConstraintOptions = {
  /** Rest length of spring */
  restLength?: number;
  /** Spring stiffness */
  stiffness?: number;
  /** Damping coefficient */
  damping?: number;
  /** Local anchor point A */
  localAnchorA?: [number, number, number] | THREE.Vector3;
  /** Local anchor point B */
  localAnchorB?: [number, number, number] | THREE.Vector3;
};

/** Union of all constraint options */
export type ConstraintOptions =
  | DistanceConstraintOptions
  | HingeConstraintOptions
  | PointToPointConstraintOptions
  | SpringConstraintOptions;

/** Options for creating a physics group */
export type CreateGroupOptions = {
  /** Group ID for identification */
  groupId?: string;
  /** Collision filter group */
  collisionFilterGroup?: number;
  /** Collision filter mask */
  collisionFilterMask?: number;
};

/** Options for pooling */
export type PoolOptions = {
  /** Initial pool size */
  initialSize?: number;
  /** Maximum pool size */
  maxSize?: number;
  /** Factory function to create new items */
  create?: () => CANNON.Body | CANNON.World;
};

/** Physics body with metadata */
export interface PhysicsBodyWithMeta {
  body: CANNON.Body;
  bodyId?: string;
  worldId?: string;
  groupId?: string;
  mesh?: THREE.Mesh | THREE.Group;
  syncOptions?: SyncMeshOptions;
}

/** Physics constraint with metadata */
export interface PhysicsConstraintWithMeta {
  constraint: CANNON.Constraint | CANNON.Spring;
  constraintId?: string;
  worldId?: string;
  bodyA: CANNON.Body;
  bodyB: CANNON.Body;
}

/** Physics group with metadata */
export interface PhysicsGroupWithMeta {
  groupId: string;
  bodies: Map<string, CANNON.Body>;
  worldId?: string;
  collisionFilterGroup: number;
  collisionFilterMask: number;
}

/** Physics world with metadata */
export interface PhysicsWorldWithMeta {
  world: CANNON.World;
  worldId: string;
  bodies: Map<string, CANNON.Body>;
  constraints: Map<string, CANNON.Constraint | CANNON.Spring>;
  groups: Map<string, PhysicsGroupWithMeta>;
  bodyPool?: CANNON.Body[];
  isRunning: boolean;
}
